// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Which published attributes make up an asset's nameplate line.
 *
 * The design asks for a rating under almost every symbol — `1200 A`,
 * `2500 kVA`, `1000 kW`, `500 kVA`, `480Y/277 V`. Against the payloads we hold,
 * **almost none of them are published**: `ratedCurrent` by nothing at all,
 * `ratedPower` by two types out of nine, `ratedVoltage` and `busbarRating` by
 * `SwitchgearType` only. See
 * `docs/power-flow/power-flow-language-data-readiness.md` §"Buses" and
 * `power-flow-data-inventory.md` §7.3.
 *
 * So this reads what is *there* and returns the rest as nothing, which
 * `Nameplate` renders as an em-dash. It deliberately does not derive, infer or
 * default a rating: a plausible number under a symbol is worse than a visible
 * gap, because an operator cannot tell the two apart.
 */

import { formatAttributeQuantity } from '../../../utils/quantity';

/** The subset of an entity this needs. Loose on purpose — attributes are untyped. */
interface NameplateSource {
  attributes?: Record<string, unknown> | null;
}

/**
 * A published attribute as nameplate text, or undefined when absent or blank.
 *
 * The twin publishes a rating either as a string (`'2000 A'`) or as a
 * `{ value, unit }` envelope (`{ value: 3000, unit: 'KiloVA' }`, which is what
 * the captured GA-10 read carries). Both are formatted the way a nameplate
 * reads — `3,000 kVA`, `12.47 kV` — rather than stringified.
 */
const attr = (entity: NameplateSource, key: string): string | undefined =>
  formatAttributeQuantity(entity.attributes?.[key]);

/**
 * Nameplate parts for this asset, in drawing order.
 *
 * Returns `undefined` when the asset has no nameplate concept at all — that
 * renders no line, as distinct from an empty array, which renders the em-dash.
 * The distinction matters: "this asset has a rating we do not have" and "a
 * rating is not a thing this asset has" are different statements.
 */
export const nameplateParts = (entity: NameplateSource): (string | undefined)[] | undefined => {
  const busbar = attr(entity, 'busbarRating');
  const voltage = attr(entity, 'ratedVoltage');
  const power = attr(entity, 'ratedPower');
  const current = attr(entity, 'ratedCurrent');

  const parts = [busbar ?? current, power, voltage].filter(part => part !== undefined);

  // Nothing published: still show the line, as an em-dash. Every asset on this
  // drawing has a nameplate in reality, so an absent one is missing data rather
  // than a category error — which is exactly what the dash is for.
  return parts.length > 0 ? parts : [];
};
